import { useInfiniteQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { DfeEnrichedTransferEntry } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

const LIMIT = 20;

export interface UseTransferHistoryReturn {
  transfers: DfeEnrichedTransferEntry[];
  total: number;
  loading: boolean;
  error: string | null;
  refresh: () => void;
  loadMore: () => void;
  hasMore: boolean;
}

export function useTransferHistory(): UseTransferHistoryReturn {
  const { data, isLoading, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: queryKeys.dfe.transferHistory({}),
      queryFn: ({ pageParam = 0 }) =>
        api.getDfeTransferHistory({ limit: LIMIT, offset: pageParam }),
      initialPageParam: 0,
      getNextPageParam: (lastPage, allPages) => {
        const nextOffset = allPages.length * LIMIT;
        return nextOffset < lastPage.total ? nextOffset : undefined;
      },
    });

  const transfers = data?.pages.flatMap((p) => p.transfers) ?? [];
  const total = data?.pages[0]?.total ?? 0;

  return {
    transfers,
    total,
    loading: isLoading,
    error: error instanceof Error ? error.message : null,
    refresh: () => {
      refetch();
    },
    loadMore: () => {
      if (hasNextPage && !isFetchingNextPage) fetchNextPage();
    },
    hasMore: !!hasNextPage,
  };
}
